type SignalDetail = { [key: string]: any } | string | undefined
type SignalConnection<T extends SignalDetail> = {
  onEmit?: (_detail: T) => void
  onDismiss?: () => void
}

/**
 * Creates a lightweight signal instance for sending one-off messages between
 * decoupled parts of the application.
 *
 * A signal is an explicit, instance-based event channel. Producers and consumers
 * must share the same signal instance in order to communicate.
 *
 * It is well-suited for coordinating behavior between step components and the
 * surrounding architecture without tight coupling or prop drilling.
 */
export function signal<T extends SignalDetail = SignalDetail>() {
  const listeners = new Set<SignalConnection<T>>()

  return {
    /**
     * Emits the signal with an optional detail payload.
     *
     * All currently subscribed `onEmit` listeners will be invoked
     * synchronously in the order they were registered.
     *
     * Note: connections added after `emit` is called will NOT be invoked
     * retroactively.
     *
     * @param detail - Optional payload describing the emit event.
     */
    emit(detail?: T) {
      for (const cb of listeners) cb.onEmit?.(detail as T)
    },

    /**
     * Emits a dismissal event for the signal.
     *
     * This is typically used to indicate that a previously emitted
     * signal is no longer relevant (e.g. step exited, flow aborted).
     *
     * All currently subscribed `onDismiss` listeners will be invoked
     * synchronously.
     */
    dismiss() {
      for (const cb of listeners) cb.onDismiss?.()
    },

    /**
     * Registers an optional `onEmit` and `onDismiss` callback to the signal.
     *
     * @param connection - A connection object or just an `onEmit` callback
     * @returns A function to disconnect the connection
     */
    connect(connection: SignalConnection<T> | ((_detail?: T) => void)) {
      if (typeof connection === 'function') {
        connection = { onEmit: connection }
      }

      listeners.add(connection)
      return () => listeners.delete(connection)
    }
  }
}
